import React, { useRef, useEffect, useState, useCallback } from 'react';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import styles from './truncatedText.module.css';

interface TruncatedTextProps {
  text: string | React.ReactNode;
  maxLines?: number;
  className?: string;
  style?: React.CSSProperties;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  showTooltip?: boolean;
  fontSize?: string;
  fontWeight?: string | number;
  color?: string;
  lineHeight?: string | number;
  width?: string | number;
}

export default function TruncatedText({
  text,
  maxLines = 1,
  className = '',
  style = {},
  tooltipPosition = 'bottom',
  showTooltip = true,
  fontSize,
  fontWeight,
  color,
  lineHeight,
  width,
}: TruncatedTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    if (!textRef.current) return;

    const element = textRef.current;

    // Simple and reliable overflow detection
    if (maxLines === 1) {
      const isOverflow = element.scrollWidth > element.clientWidth;
      setIsOverflowing(isOverflow);
    } else {
      const isOverflow = element.scrollHeight > element.clientHeight;
      setIsOverflowing(isOverflow);
    }
  }, [maxLines]);

  useEffect(() => {
    const timeoutId = setTimeout(checkOverflow, 100);

    const handleResize = () => {
      setTimeout(checkOverflow, 50);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [text, maxLines, checkOverflow]);

  const textStyles: React.CSSProperties = {
    fontSize,
    fontWeight,
    color,
    lineHeight: lineHeight || '1.4',
    maxWidth: width,
    ...style,
  };

  const truncationStyles: React.CSSProperties =
    maxLines === 1
      ? {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }
      : {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        };

  const truncatedTextElement = (
    <div
      ref={textRef}
      className={`${styles.truncatedText} ${className}`}
      style={{
        ...textStyles,
        ...truncationStyles,
      }}
      title={!showTooltip && isOverflowing ? (text as string) : undefined}
    >
      {text}
    </div>
  );

  // Only show tooltip if text is overflowing and showTooltip is true
  if (showTooltip && isOverflowing) {
    return (
      <CustomTooltip title={text as string} position={tooltipPosition} multiline>
        {truncatedTextElement}
      </CustomTooltip>
    );
  }

  return truncatedTextElement;
}
